/**
 * EmployeeTableSection.tsx - Employee statistics + payroll data table
 *
 * Phase 2: payroll-management.tsx component decomposition
 * FR-10: Extracted JSX section
 */

import React from 'react';
import { usePayrollRender } from './PayrollRenderContext.tsx';
import { recalcOvertimePay, PART_LOCKED_FIELDS, isPartPosition } from '../../utils/payrollCalculations.ts';
import { POSITION_SORT_ORDER } from '../../utils/payrollEmployeeUtils.ts';

export const EmployeeTableSection: React.FC = () => {
  const {
    SaveStatusIcon,
    addEmployee,
    allChecked,
    applyOvertimeMultiplier,
    budgetData,
    calculateDeductions,
    calculateNetSalaryWithAllowances,
    calculateRealTimeIndividualIncentive,
    canEditPayroll,
    canEditField,
    payrollEditMode,
    checkedItems,
    deleteEmployees,
    deleteEmployeesGlobally,
    employees,
    getFieldStyle,
    getSortedTeams,
    globalEmployees,
    handleImportPreviousMonth,
    importedFromMonth,
    isComposing,
    localTeams,
    previousMonthEmployees,
    renderMonthlyComparison,
    renderPayrollDataCell,
    renderSummaryPayrollDataCell,
    selectedMonth,
    setApplyOvertimeMultiplier,
    setEmployees,
    setIsComposing,
    setIsDataChanged,
    setShowComparisonWithPreviousMonth,
    setTempNameValues,
    shouldMaskTeamData,
    showComparisonWithPreviousMonth,
    teamSortType,
    tempNameValues,
    toggleAllChecked,
    toggleChecked,
    updateAllowance,
    updateEmployeeFamilyCount,
    updateEmployeeName,
    updateEmployeePosition,
    updateDirectorIncentiveRate,
    updateDirectorTeamAllocation,
    updateEmployeeSalary,
    updateEmployeeTeam,
    updateNonTaxable,
    updateOvertimeMinutes,
    user,
    userTeamName,
  } = usePayrollRender();

  return (
    <>
      {/* 월별 직원 통계 컴포넌트 */}
      <div className="bg-white p-2 sm:p-3 md:p-4 rounded-lg shadow-sm mb-2 sm:mb-3">
        <h2 className="text-base sm:text-lg font-medium mb-3">월별 직원 구성 통계</h2>
        <div className="flex flex-wrap -mx-2">
          {/* 현재 월 직원 통계 */}
          <div className="w-full md:w-1/2 lg:w-1/3 px-2 mb-4">
            <div className="border rounded-lg p-3 bg-blue-50">
              <h3 className="text-md font-medium text-blue-700 mb-2">{selectedMonth.replace('-', '년 ')}월 직원 구성</h3>
              
              <div className="space-y-2">
                {(() => {
                  const paidEmployees = employees.filter(emp => emp.salary > 0);
                  const excludedCount = employees.length - paidEmployees.length;
                  return (
                    <>
                      <div className="flex justify-between">
                        <span className="text-sm">총 직원 수:</span>
                        <span className="font-semibold">
                          {paidEmployees.length}명
                          {excludedCount > 0 && <span className="text-xs text-gray-500 ml-1">(급여 0원 {excludedCount}명 제외)</span>}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-sm">팀장:</span>
                        <span className="font-semibold">{paidEmployees.filter(emp => emp.position === '팀장').length}명</span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-sm">팀원:</span>
                        <span className="font-semibold">{paidEmployees.filter(emp => emp.position === '팀원').length}명</span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-sm">평균 급여:</span>
                        <span className="font-semibold">
                          {paidEmployees.length > 0
                            ? Math.round(paidEmployees.reduce((sum, emp) => sum + emp.salary, 0) / paidEmployees.length).toLocaleString()
                            : 0}원
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
          
          {renderMonthlyComparison()}
        </div>
      </div>
      
      {/* 인건비 내역 테이블 */}
      <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm mb-3">
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">인건비 내역</h2>
                <span className="text-sm text-blue-800 font-medium bg-blue-100 px-3 py-1 rounded-full">
                  {selectedMonth.replace('-', '년 ')}월
                </span>
              </div>
              {(() => {
                // 현재 선택된 월에 데이터가 있는 경우
                if (budgetData.monthlyData?.[selectedMonth]?.employees) {
                  // importedFromMonth가 있고 현재 선택된 월과 다르면 "가져온 데이터" 표시
                  if (importedFromMonth && importedFromMonth !== selectedMonth) {
                    return (
                      <span className="text-xs text-blue-800 mt-2 p-2 bg-blue-50 rounded-md border border-blue-200">
                        📋 {importedFromMonth?.replace('-', '년 ')}월에서 가져온 데이터입니다
                      </span>
                    );
                  } else {
                    // 현재 월 데이터가 있으면 "현재 월 데이터" 표시
                    return (
                      <span className="text-xs text-green-800 mt-2 p-2 bg-green-50 rounded-md border border-green-200">
                        ✅ {selectedMonth.replace('-', '년 ')}월 데이터를 불러왔습니다
                      </span>
                    );
                  }
                } else if (globalEmployees?.length > 0) {
                  // 전역 데이터를 사용하는 경우
                  return (
                    <span className="text-xs text-yellow-800 mt-2 p-2 bg-yellow-50 rounded-md border border-yellow-200">
                      🌍 전역 직원 데이터를 사용 중입니다
                    </span>
                  );
                }
                return null;
              })()}
            </div>
            
            <div className="text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
              직원별 급여, 수당, 인센티브를 관리합니다
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              className={`py-2 px-4 text-sm rounded-lg font-medium transition-colors ${
                showComparisonWithPreviousMonth
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
              }`}
              onClick={() => setShowComparisonWithPreviousMonth(!showComparisonWithPreviousMonth)}
            >
              {showComparisonWithPreviousMonth ? '지난달 비교 숨기기' : '지난달 비교 보기'}
            </button>
            {/* 🔒 전체 편집 권한 전용. 이 버튼은 로스터·목표매출·인건비합계를 통째로
                Firestore에 덮어쓴다 — 제한 권한(실장)에게 열어두면 클릭 한 번으로
                그 달 급여 전체가 지난달 값으로 바뀐다. 부분 저장을 만든 이유가 사라진다. */}
            {payrollEditMode === 'full' && (
              <button
                onClick={handleImportPreviousMonth}
                className="py-1 px-2 sm:py-2 sm:px-4 text-xs bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                title="지난달 인건비 내역 불러오기"
              >
                지난달 내역 불러오기
              </button>
            )}
            {/* 🔒 인원 변경은 전체 권한 전용. 제한 권한 저장은 추가·삭제를 무시하므로
                열어두면 "추가했는데 저장이 안 된다"는 혼란만 남는다. */}
            {payrollEditMode === 'full' && (
              <>
                <button
                  className="bg-blue-600 hover:bg-blue-700 text-white py-1 px-2 sm:py-2 sm:px-4 text-xs sm:text-sm rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  onClick={addEmployee}
                >
                  팀원 추가
                </button>
                <button
                  className="bg-red-600 hover:bg-red-700 text-white py-1 px-2 sm:py-2 sm:px-4 text-xs sm:text-sm rounded-lg disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                  onClick={deleteEmployees}
                  disabled={!Object.values(checkedItems).some(item => item)}
                >
                  선택 삭제
                </button>
              </>
            )}
            {user?.role === 'admin' && (
              <button
                className="bg-red-800 hover:bg-red-900 text-white py-1 px-2 sm:py-2 sm:px-4 text-xs sm:text-sm rounded-lg border border-red-900 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                onClick={deleteEmployeesGlobally}
                disabled={!Object.values(checkedItems).some(item => item)}
                title="선택 직원을 직원 마스터(전역)에서 완전히 삭제"
              >
                전역 삭제
              </button>
            )}

            {/* 연장근로수당 150% 가산 토글 */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-orange-50 border-orange-200">
              <span className="font-medium text-orange-700 text-xs sm:text-sm">초과수당 150% 가산</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={applyOvertimeMultiplier}
                  onChange={(e) => {
                    // 수동 override: 다음 인원 편집(자동 재판정) 전까지 유지됨
                    setApplyOvertimeMultiplier(e.target.checked);
                    // 토글 상태 변경 시 모든 직원의 초과수당 재계산 (공통 헬퍼, 파트 포함)
                    setEmployees(recalcOvertimePay(employees, e.target.checked));
                    setIsDataChanged(true);
                  }}
                  className="sr-only peer"
                  disabled={!canEditPayroll}
                />
                <div className="w-8 h-4 bg-gray-200 peer-focus:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-1 rounded-full peer peer-checked:bg-orange-500 transition-all"></div>
                <div className="absolute left-0.5 top-0.5 bg-white w-3 h-3 rounded-full transition-all peer-checked:translate-x-4"></div>
              </label>
              <span className="text-xs text-gray-600 ml-1">(5인 이상 사업장)</span>
              {!canEditPayroll && (
                <span className="text-xs text-gray-500 ml-2">🔒 읽기 전용</span>
              )}
            </div>
            
            {/* 동일인 통합 기능은 자동 감지 방식으로 변경 */}
            {/* 인건비 전체 저장 버튼은 상단 통합 버튼 사용 */}
          </div>
        </div>
        <div className="overflow-x-auto w-full -mx-2 sm:-mx-3 md:-mx-0 px-2 sm:px-3 md:px-0">
          <table className="min-w-full bg-white text-xs">
            <thead>
              <tr className="bg-gray-50 text-gray-700 text-xs leading-normal">
                <th className="py-1 px-1 text-center">
                  <input type="checkbox" checked={allChecked} onChange={toggleAllChecked} className="h-4 w-4" />
                </th>
                <th className="py-1 px-1 text-center">팀</th>
                <th className="py-1 px-1 text-center">직위</th>
                <th className="py-1 px-1 text-center">이름</th>
                <th className="py-1 px-1 text-center">가족수</th>
                <th className="py-1 px-1 text-right">비과세총액</th>
                <th className="py-1 px-1 text-right">
                  월급(세전)
                  {budgetData.monthlyData && (() => {
                    // 이전 월 데이터 불러오기 (가장 최근 1개)
                    const prevMonth = Object.keys(budgetData.monthlyData || {})
                      .filter(month => month !== selectedMonth && budgetData.monthlyData?.[month].employees)
                      .sort((a, b) => b.localeCompare(a))[0];

                    return prevMonth ?
                      <span className="block text-xs text-gray-500 font-normal">(이전월 대비)</span> :
                      null;
                  })()}
                </th>
                <th className="py-1 px-1 text-right">공제액합계(세금)</th>
                <th className="py-1 px-1 text-right">월급(세후)</th>
                <th className="py-1 px-1 text-right">인센티브</th>
                <th className="py-1 px-1 text-right">퇴직금</th>
                <th className="py-1 px-1 text-right">식대</th>
                <th className="py-1 px-1 text-right">기숙사</th>
                <th className="py-1 px-1 text-center">연장근로시간</th>
                <th className="py-1 px-1 text-right">초과수당</th>
                <th className="py-1 px-1 text-right">개별수당</th>
                <th className="py-1 px-1 text-right">기타</th>
                <th className="py-1 px-1 text-right">인건비(세전)</th>
                <th className="py-1 px-1 text-right">실지급액(세금)</th>
              </tr>
            </thead>
            <tbody className="text-gray-600 text-sm">
              {employees
                .slice() // 원본 배열 변경 방지를 위해 복사본 사용
                .sort((a, b) => {
                  const sortedTeamNames = getSortedTeams([a.team, b.team], teamSortType);
                  if (a.team !== b.team) return sortedTeamNames[0] === a.team ? -1 : 1;
                  const aOrder = POSITION_SORT_ORDER[a.position] ?? 2;
                  const bOrder = POSITION_SORT_ORDER[b.position] ?? 2;
                  if (aOrder !== bOrder) return aOrder - bOrder;
                  return a.name.localeCompare(b.name);
                })
                .map((employee) => {
                // 지난달 데이터 비교를 위한 직원 찾기
                const prevMonthEmployee = showComparisonWithPreviousMonth && previousMonthEmployees.length > 0
                  ? previousMonthEmployees.find(emp => emp.name === employee.name)
                  : null;
                
                // 변경 여부에 따라 스타일 지정 함수
                const getComparisonStyle = (current: number, previous?: number) => {
                  if (!showComparisonWithPreviousMonth || !previous) return '';
                  const diff = current - previous;
                  if (Math.abs(diff) < 1) return '';
                  return diff > 0 ? 'bg-green-50 border-green-300' : diff < 0 ? 'bg-red-50 border-red-300' : '';
                };
                
                // 직원 역할 사용자가 다른 팀 데이터를 볼 때 마스킹 여부 확인
                // 실장 데이터는 admin만 볼 수 있음 (position 파라미터 전달)
                // 🔗 employee.id를 넘겨야 본인 판정이 선다. 빠뜨리면 실장 본인 행 전체가
                //    **** 로 덮이고, 그 행의 연장근로시간·개별수당 입력칸까지 사라진다.
                const shouldMask = shouldMaskTeamData(employee.team, employee.position, employee.id);

                // 🔒 파트는 개별수당으로만 급여를 지급한다(정책) — 고정 급여 입력은 잠근다.
                //    잠긴 칸에 값이 남아 매달 이월되는 것을 막기 위해 직위 전환 시 0으로 정리된다.
                const isPart = isPartPosition(employee);
                const OPEN = { disabled: false, title: undefined as string | undefined, className: '' };
                const LOCKED = { disabled: true, title: '파트는 개별수당으로 입력합니다', className: ' bg-gray-100 text-gray-500 cursor-not-allowed' };
                //    ⚠️ 잠금 대상은 반드시 정본 목록(PART_LOCKED_FIELDS)에서 판정한다. "파트면 전부"로
                //    판단했다가 개별수당까지 잠겨, 2026-08-04부터 아르바이트 비용을 넣을 수 없었다.
                const partLockFor = (field: string) =>
                  isPart && (PART_LOCKED_FIELDS as readonly string[]).includes(field) ? LOCKED : OPEN;

                // 🔒 권한 잠금 — 제한 권한(실장)은 연장근로시간·개별수당만 입력할 수 있다.
                //    파트 잠금과 독립이며 둘 중 하나라도 걸리면 잠긴다.
                const lockField = (field: string) => {
                  const allowed = typeof canEditField === 'function' ? canEditField(field) : true;
                  if (allowed) return partLockFor(field);
                  return {
                    disabled: true,
                    title: payrollEditMode === 'limited'
                      ? '연장근로시간·개별수당만 입력할 수 있습니다'
                      : '급여 편집 권한이 없습니다',
                    className: ' bg-gray-100 text-gray-500 cursor-not-allowed',
                  };
                };

                // 🔒 권한 전용 잠금 — 팀·직위·이름처럼 파트 정책과 무관한 칸에 쓴다.
                //    여기에 lockField를 쓰면 파트 직원의 팀·직위까지 잠겨버린다.
                const permLock = (field: string) => {
                  const allowed = typeof canEditField === 'function' ? canEditField(field) : true;
                  if (allowed) return { disabled: false, title: undefined as string | undefined, className: '' };
                  return {
                    disabled: true,
                    title: payrollEditMode === 'limited'
                      ? '연장근로시간·개별수당만 입력할 수 있습니다'
                      : '급여 편집 권한이 없습니다',
                    className: ' bg-gray-100 text-gray-500 cursor-not-allowed',
                  };
                };
                
                return (
                  <React.Fragment key={employee.id || employee.name}>
                  <tr className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="py-1 px-1 text-center">
                      <input type="checkbox" checked={checkedItems[employee.id || employee.name] || false} onChange={() => toggleChecked(employee.id || employee.name)} className="h-4 w-4" />
                    </td>
                    <td className="py-1 px-1 text-center">
                      <select 
                        value={employee.team} 
                        onChange={(e) => updateEmployeeTeam(employee.id || employee.name, e.target.value)} 
                        disabled={permLock('team').disabled}
                        title={permLock('team').title}
                        className={`border p-1 ${showComparisonWithPreviousMonth && prevMonthEmployee && employee.team !== prevMonthEmployee.team ? 'bg-yellow-50 border-yellow-300' : ''}${permLock('team').className}`}
                      >
                        {localTeams.map(team => (<option key={team} value={team}>{team}</option>))}
                        <option value="미지정">미지정</option>
                      </select>
                      {showComparisonWithPreviousMonth && prevMonthEmployee && employee.team !== prevMonthEmployee.team && (
                        <div className="text-xs text-gray-600 mt-1">이전: {prevMonthEmployee.team}</div>
                      )}
                    </td>
                    <td className="py-1 px-1 text-center">
                      <select 
                        value={employee.position} 
                        onChange={(e) => updateEmployeePosition(employee.id || employee.name, e.target.value)}
                        disabled={permLock('position').disabled}
                        title={permLock('position').title}
                        className={`border p-1 ${showComparisonWithPreviousMonth && prevMonthEmployee && employee.position !== prevMonthEmployee.position ? 'bg-yellow-50 border-yellow-300' : ''}${permLock('position').className}`}
                      >
                        <option value="팀장">팀장</option>
                        <option value="팀원">팀원</option>
                        <option value="실장">실장</option>
                        <option value="파트">파트</option>
                      </select>
                      {showComparisonWithPreviousMonth && prevMonthEmployee && employee.position !== prevMonthEmployee.position && (
                        <div className="text-xs text-gray-600 mt-1">이전: {prevMonthEmployee.position}</div>
                      )}
                    </td>
                    <td className="py-1 px-1 text-center">
                      {shouldMask ? (
                        <span className="text-gray-500">****</span>
                      ) : (
                        <>
                          <input 
                            type="text" 
                            value={(() => {
                              const employeeKey = employee.id || employee.name;
                              return isComposing[employeeKey] ? (tempNameValues[employeeKey] ?? employee.name) : employee.name;
                            })()} 
                            onChange={(e) => {
                              const employeeKey = employee.id || employee.name;
                              const value = e.target.value;
                              
                              if (isComposing[employeeKey]) {
                                // 한국어 입력 중에는 임시값만 저장
                                setTempNameValues(prev => ({...prev, [employeeKey]: value}));
                              } else {
                                // 한국어 입력이 아닐 때는 바로 업데이트
                                updateEmployeeName(employee.id || employee.name, value);
                              }
                            }}
                            onCompositionStart={(e) => {
                              const employeeKey = employee.id || employee.name;
                              setIsComposing(prev => ({...prev, [employeeKey]: true}));
                              setTempNameValues(prev => ({...prev, [employeeKey]: e.currentTarget?.value || employee.name}));
                            }}
                            onCompositionEnd={(e) => {
                              const employeeKey = employee.id || employee.name;
                              const finalValue = e.currentTarget?.value || employee.name;
                              
                              setIsComposing(prev => ({...prev, [employeeKey]: false}));
                              setTempNameValues(prev => {
                                const newTemp = {...prev};
                                delete newTemp[employeeKey];
                                return newTemp;
                              });
                              updateEmployeeName(employee.id || employee.name, finalValue);
                            }}
                            disabled={permLock('name').disabled}
                            title={permLock('name').title}
                            className={`border p-1 w-16 text-center${permLock('name').className}`}
                            style={getFieldStyle(`name_${employee.id}`)}
                          />
                          <SaveStatusIcon fieldId={`name_${employee.id}`} className="ml-1" />
                        </>
                      )}
                    </td>
                    <td className="py-1 px-1 text-center">
                      {shouldMask ? (
                        <span className="text-gray-500">****</span>
                      ) : (
                        <>
                          <input
                            type="number"
                            value={employee.familyCount}
                            onChange={(e) => updateEmployeeFamilyCount(employee.id || employee.name, e.target.value)}
                            min="0" max="10"
                            disabled={lockField('familyCount').disabled}
                            title={lockField('familyCount').title}
                            className={`border p-1 w-16 text-center ${getComparisonStyle(employee.familyCount, prevMonthEmployee?.familyCount)}${lockField('familyCount').className}`}
                            style={getFieldStyle(`familyCount_${employee.id}`)}
                          />
                          <SaveStatusIcon fieldId={`familyCount_${employee.id}`} className="ml-1" />
                          {showComparisonWithPreviousMonth && prevMonthEmployee && employee.familyCount !== prevMonthEmployee.familyCount && (
                            <div className="text-xs text-gray-600 mt-1">이전: {prevMonthEmployee.familyCount}</div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="py-1 px-1 text-center">
                      {shouldMask ? (
                        <span className="text-gray-500">****</span>
                      ) : (
                        <input
                          type="text"
                          value={(employee.nonTaxable || 0).toLocaleString()}
                          onChange={(e) => updateNonTaxable(employee.id || employee.name, e.target.value)}
                          disabled={lockField('nonTaxable').disabled}
                          title={lockField('nonTaxable').title}
                          className={`border p-1 w-20 text-right tabular-nums ${getComparisonStyle(employee.nonTaxable || 0, prevMonthEmployee?.nonTaxable)}${lockField('nonTaxable').className}`}
                          style={getFieldStyle(`nonTaxable_${employee.id}`)}
                        />
                      )}
                      <SaveStatusIcon fieldId={`nonTaxable_${employee.id}`} className="ml-1" />
                      {showComparisonWithPreviousMonth && prevMonthEmployee && (employee.nonTaxable || 0) !== (prevMonthEmployee.nonTaxable || 0) && (
                        <div className="text-xs text-gray-600 mt-1">이전: {(prevMonthEmployee.nonTaxable || 0).toLocaleString()}</div>
                      )}
                    </td>
                    <td className="py-1 px-1 text-center">
                      {shouldMask ? (
                        <span className="text-gray-500">****</span>
                      ) : (
                        <div className="relative">
                          <input
                            type="text"
                            value={employee.salary.toLocaleString()}
                            onChange={(e) => updateEmployeeSalary(employee.id || employee.name, e.target.value)}
                            disabled={lockField('salary').disabled}
                            title={lockField('salary').title}
                            className={`border p-1 w-24 text-right tabular-nums ${getComparisonStyle(employee.salary, prevMonthEmployee?.salary)}${lockField('salary').className}`}
                            style={getFieldStyle(`salary_${employee.id}`)}
                          />
                          <SaveStatusIcon fieldId={`salary_${employee.id}`} className="ml-1" />
                          {showComparisonWithPreviousMonth && prevMonthEmployee && employee.salary !== prevMonthEmployee.salary && (
                            <div className="text-xs text-gray-600 mt-1">
                              이전: {prevMonthEmployee.salary.toLocaleString()}
                              {prevMonthEmployee.salary > 0 ? 
                                ` (${((employee.salary - prevMonthEmployee.salary) / prevMonthEmployee.salary * 100).toFixed(1)}%)` 
                                : ''}
                            </div>
                          )}
                          {/* 이전 월과 급여 비교 */}
                          {(() => {
                            if (!budgetData.monthlyData) return null;
                            
                            // 이전 월 데이터 불러오기 (가장 최근 1개)
                            const prevMonth = Object.keys(budgetData.monthlyData || {})
                              .filter(month => month !== selectedMonth && budgetData.monthlyData?.[month].employees)
                              .sort((a, b) => b.localeCompare(a))[0];
                            
                            if (!prevMonth) return null;
                            
                            // 이전 월에 같은 ID 직원 찾기
                            const prevMonthEmployee = budgetData.monthlyData[prevMonth].employees?.find(emp => emp.id === employee.id);
                            if (!prevMonthEmployee) return null;
                            
                            // 급여 변동 계산
                            const salaryDiff = employee.salary - prevMonthEmployee.salary;
                            const salaryDiffPercent = (salaryDiff / prevMonthEmployee.salary) * 100;
                            
                            if (Math.abs(salaryDiff) < 1) return null;
                            
      return (
                              <div className={`absolute -bottom-4 right-0 text-xs font-medium ${
                                salaryDiff > 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {salaryDiff > 0 ? '▲' : '▼'} {Math.abs(salaryDiffPercent).toFixed(1)}%
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </td>
                    <td className="py-1 px-1 text-right tabular-nums font-semibold">
                      {renderPayrollDataCell(employee.id || employee.name, 'tax', employee.tax.toLocaleString(), employee.team, 'text-gray-900 font-semibold', '', employee.position)}
                    </td>
                    <td className="py-1 px-1 text-right tabular-nums font-semibold">
                      {renderPayrollDataCell(employee.id || employee.name, 'netSalary', employee.netSalary.toLocaleString(), employee.team, 'text-gray-900 font-semibold', '', employee.position)}
                    </td>
                    <td className="py-1 px-1 text-right tabular-nums font-semibold text-gray-900">
                      {shouldMask ? (
                        <span className="text-gray-500">****</span>
                      ) : (
                        <div className="relative">
                          {calculateRealTimeIndividualIncentive(employee).toLocaleString()}
                          {/* 이전 월과 인센티브 비교 */}
                          {(() => {
                            if (!budgetData.monthlyData) return null;
                            
                            // 이전 월 데이터 불러오기 (가장 최근 1개)
                            const prevMonth = Object.keys(budgetData.monthlyData || {})
                              .filter(month => month !== selectedMonth && budgetData.monthlyData?.[month].employees)
                              .sort((a, b) => b.localeCompare(a))[0];
                            
                            if (!prevMonth) return null;
                            
                            // 이전 월에 같은 ID 직원 찾기
                            const prevMonthEmployee = budgetData.monthlyData[prevMonth].employees?.find(emp => emp.id === employee.id);
                            if (!prevMonthEmployee) return null;
                            
                            // 인센티브 변동 계산
                            const incentiveDiff = (employee.incentive || 0) - (prevMonthEmployee.incentive || 0);
                            if (Math.abs(incentiveDiff) < 1) return null;
                            
                            if ((prevMonthEmployee.incentive || 0) === 0 && (employee.incentive || 0) > 0) {
                              return (
                                <div className="absolute -bottom-4 right-0 text-xs font-medium text-green-600">
                                  신규
                                </div>
                              );
                            }
                            
                            const incentiveDiffPercent = prevMonthEmployee.incentive ? 
                              (incentiveDiff / prevMonthEmployee.incentive) * 100 : 
                              0;
                            
                            return (
                              <div className={`absolute -bottom-4 right-0 text-xs font-medium ${
                                incentiveDiff > 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {incentiveDiff > 0 ? '▲' : '▼'} {Math.abs(incentiveDiffPercent).toFixed(1)}%
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </td>
                    <td className="py-1 px-1 text-right tabular-nums text-sm text-gray-600">
                      {renderPayrollDataCell(employee.id || employee.name, 'retirement', (employee.retirement || 0).toLocaleString(), employee.team, 'text-sm text-gray-600', '', employee.position)}
                    </td>
                    <td className="py-1 px-1 text-center">
                      {shouldMask ? (
                        <span className="text-gray-500">****</span>
                      ) : (
                        <>
                          <input type="text" value={(employee.meal || 0).toLocaleString()} onChange={(e) => updateAllowance(employee.id, 'meal', e.target.value)} disabled={lockField('meal').disabled} title={lockField('meal').title} className={`border p-1 w-16 text-right tabular-nums${lockField('meal').className}`} style={getFieldStyle(`meal_${employee.id}`)} />
                          <SaveStatusIcon fieldId={`meal_${employee.id}`} className="ml-1" />
                        </>
                      )}
                    </td>
                    <td className="py-1 px-1 text-center">
                      {shouldMask ? (
                        <span className="text-gray-500">****</span>
                      ) : (
                        <>
                          <input type="text" value={(employee.housing || 0).toLocaleString()} onChange={(e) => updateAllowance(employee.id, 'housing', e.target.value)} disabled={lockField('housing').disabled} title={lockField('housing').title} className={`border p-1 w-16 text-right tabular-nums${lockField('housing').className}`} style={getFieldStyle(`housing_${employee.id}`)} />
                          <SaveStatusIcon fieldId={`housing_${employee.id}`} className="ml-1" />
                        </>
                      )}
                    </td>
                    
                    <td className="py-1 px-1 text-center">
                      {shouldMask ? (
                        <span className="text-gray-500">****</span>
                      ) : (
                        <>
                          <input
                            type="text"
                            value={employee.overtimeMinutes ? `${employee.overtimeMinutes}분` : '0분'}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^0-9]/g, '');
                              updateOvertimeMinutes(employee.id, value);
                            }}
                            disabled={lockField('overtimeMinutes').disabled}
                            className={`border p-1 w-16 text-center${lockField('overtimeMinutes').className}`}
                            style={getFieldStyle(`overtimeMinutes_${employee.id}`)}
                            placeholder="0분"
                            title={lockField('overtimeMinutes').title ?? '연장근로시간을 분 단위로 입력하세요 (30분 단위 권장)'}
                          />
                          <SaveStatusIcon fieldId={`overtimeMinutes_${employee.id}`} className="ml-1" />
                        </>
                      )}
                    </td>
                    <td className="py-1 px-1 text-center">
                      {shouldMask ? (
                        <span className="text-gray-500">****</span>
                      ) : (
                        <>
                          <input 
                            type="text" 
                            value={(employee.overtime || 0).toLocaleString()}
                            className="border p-1 w-16 text-right tabular-nums bg-gray-100"
                            disabled
                            title="연장근로시간으로 자동 계산됩니다" style={getFieldStyle(`overtime_${employee.id}`)} />
                          <SaveStatusIcon fieldId={`overtime_${employee.id}`} className="ml-1" />
                        </>
                      )}
                    </td>
                    <td className="py-1 px-1 text-center">
                      {shouldMask ? (
                        <span className="text-gray-500">****</span>
                      ) : (
                        <>
                          <input type="text" value={(employee.individual || 0).toLocaleString()} onChange={(e) => updateAllowance(employee.id, 'individual', e.target.value)} disabled={lockField('individual').disabled} title={lockField('individual').title} className={`border p-1 w-16 text-right tabular-nums${lockField('individual').className}`} style={getFieldStyle(`individual_${employee.id}`)} />
                          <SaveStatusIcon fieldId={`individual_${employee.id}`} className="ml-1" />
                        </>
                      )}
                    </td>
                    <td className="py-1 px-1 text-center">
                      {shouldMask ? (
                        <span className="text-gray-500">****</span>
                      ) : (
                        <>
                          <input type="text" value={(employee.other || 0).toLocaleString()} onChange={(e) => updateAllowance(employee.id, 'other', e.target.value)} disabled={lockField('other').disabled} title={lockField('other').title} className={`border p-1 w-16 text-right tabular-nums${lockField('other').className}`} style={getFieldStyle(`other_${employee.id}`)} />
                          <SaveStatusIcon fieldId={`other_${employee.id}`} className="ml-1" />
                        </>
                      )}
                    </td>
                    <td className="py-1 px-1 text-right tabular-nums font-semibold text-gray-900">
                      {shouldMask ? '****' : (employee.salary + calculateRealTimeIndividualIncentive(employee) + (employee.overtime || 0) + (employee.individual || 0)).toLocaleString()}
                    </td>
                    <td className="py-1 px-1 text-right tabular-nums font-semibold">
                      {(() => {
                        const netSalaryWithAllowancesTax = calculateNetSalaryWithAllowances(employee);
                        const allowancesTax = calculateDeductions(employee, true).tax;
                        const displayValue = (
                          <>{netSalaryWithAllowancesTax.toLocaleString()} <span className="text-xs text-red-600">({allowancesTax.toLocaleString()})</span></>
                        );
                        return renderPayrollDataCell(employee.id || employee.name, 'netSalaryWithTax', displayValue, employee.team, 'font-semibold text-gray-900', '', employee.position);
                      })()}
                    </td>
                  </tr>
                  {employee.position === '실장' && (
                    <tr key={`director-settings-${employee.id}`} className="bg-purple-50 border-b border-purple-200">
                      <td colSpan={20} className="py-2 px-3">
                        <div className="flex flex-wrap items-center gap-4 text-xs">
                          <span className="font-semibold text-purple-700">실장 설정</span>
                          <div className="flex items-center gap-1">
                            <label className="text-gray-600">인센티브 선공제율(%):</label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              value={employee.directorIncentiveRate || 0}
                              onChange={(e) => updateDirectorIncentiveRate(employee.id || employee.name, Number(e.target.value))}
                              disabled={permLock('directorIncentiveRate').disabled}
                              title={permLock('directorIncentiveRate').title}
                              className={`border rounded px-1 py-0.5 w-16 text-center${permLock('directorIncentiveRate').className}`}
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-gray-600">팀별 인건비 배분(%):</label>
                            {localTeams.filter(t => t !== '미지정').map(teamName => (
                              <span key={teamName} className="flex items-center gap-0.5">
                                <span className="text-gray-500">{teamName}:</span>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="1"
                                  value={employee.teamCostAllocation?.[teamName] || 0}
                                  onChange={(e) => updateDirectorTeamAllocation(employee.id || employee.name, teamName, Number(e.target.value))}
                                  disabled={permLock('teamCostAllocation').disabled}
                                  title={permLock('teamCostAllocation').title}
                                  className={`border rounded px-1 py-0.5 w-12 text-center${permLock('teamCostAllocation').className}`}
                                />
                              </span>
                            ))}
                            <span className="text-gray-600 ml-1">
                              (합계: {localTeams.filter(t => t !== '미지정').reduce((s: number, t: string) => s + (Number(employee.teamCostAllocation?.[t]) || 0), 0)}%)
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
              {/* 합계 행 */}
              <tr className="bg-blue-50 font-semibold text-blue-800">
                <td colSpan={5} className="py-1 px-1 text-right">
                  {user?.role === 'employee' && userTeamName ? `${userTeamName} 팀 합계 :` : '합계 :'}
                </td>
                <td className="py-1 px-1 text-right tabular-nums">
                  {employees
                    .filter(emp => user?.role !== 'employee' || emp.team === userTeamName)
                    .reduce((sum, emp) => sum + (emp.nonTaxable || 0), 0).toLocaleString()}
                </td>
                <td className="py-1 px-1 text-right tabular-nums">
                  {employees
                    .filter(emp => user?.role !== 'employee' || emp.team === userTeamName)
                    .reduce((sum, emp) => sum + emp.salary, 0).toLocaleString()}
                </td>
                <td className="py-1 px-1 text-right tabular-nums">
                  {renderSummaryPayrollDataCell(
                    'tax',
                    employees
                      .filter(emp => user?.role !== 'employee' || emp.team === userTeamName)
                      .reduce((sum, emp) => sum + emp.tax, 0).toLocaleString(),
                    ''
                  )}
                </td>
                <td className="py-1 px-1 text-right tabular-nums">
                  {renderSummaryPayrollDataCell(
                    'netSalary',
                    employees
                      .filter(emp => user?.role !== 'employee' || emp.team === userTeamName)
                      .reduce((sum, emp) => sum + emp.netSalary, 0).toLocaleString(),
                    ''
                  )}
                </td>
                <td className="py-1 px-1 text-right tabular-nums">
                  {employees
                    .filter(emp => user?.role !== 'employee' || emp.team === userTeamName)
                    .reduce((sum, emp) => sum + calculateRealTimeIndividualIncentive(emp), 0).toLocaleString()}
                </td>
                <td className="py-1 px-1 text-right tabular-nums">
                  {renderSummaryPayrollDataCell(
                    'retirement',
                    employees
                      .filter(emp => user?.role !== 'employee' || emp.team === userTeamName)
                      .reduce((sum, emp) => sum + (emp.retirement || 0), 0).toLocaleString(),
                    ''
                  )}
                </td>
                <td className="py-1 px-1 text-right tabular-nums">
                  {employees
                    .filter(emp => user?.role !== 'employee' || emp.team === userTeamName)
                    .reduce((sum, emp) => sum + (emp.meal || 0), 0).toLocaleString()}
                </td>
                <td className="py-1 px-1 text-right tabular-nums">
                  {employees
                    .filter(emp => user?.role !== 'employee' || emp.team === userTeamName)
                    .reduce((sum, emp) => sum + (emp.housing || 0), 0).toLocaleString()}
                </td>

                <td className="py-1 px-1 text-center">
                  {employees
                    .filter(emp => user?.role !== 'employee' || emp.team === userTeamName)
                    .reduce((sum, emp) => sum + (emp.overtimeMinutes || 0), 0).toLocaleString()} 분
                </td>
                <td className="py-1 px-1 text-right tabular-nums">
                  {employees
                    .filter(emp => user?.role !== 'employee' || emp.team === userTeamName)
                    .reduce((sum, emp) => sum + (emp.overtime || 0), 0).toLocaleString()}
                </td>
                <td className="py-1 px-1 text-right tabular-nums">
                  {employees
                    .filter(emp => user?.role !== 'employee' || emp.team === userTeamName)
                    .reduce((sum, emp) => sum + (emp.individual || 0), 0).toLocaleString()}
                </td>
                <td className="py-1 px-1 text-right tabular-nums">
                  {employees
                    .filter(emp => user?.role !== 'employee' || emp.team === userTeamName)
                    .reduce((sum, emp) => sum + (emp.other || 0), 0).toLocaleString()}
                </td>
                <td className="py-1 px-1 text-right tabular-nums font-semibold">
                  {(() => {
                    const filteredEmployees = employees.filter(emp => user?.role !== 'employee' || emp.team === userTeamName);
                    const filteredSalary = filteredEmployees.reduce((sum, emp) => sum + emp.salary, 0);
                    const filteredIncentive = filteredEmployees.reduce((sum, emp) => sum + calculateRealTimeIndividualIncentive(emp), 0);
                    const filteredOvertime = filteredEmployees.reduce((sum, emp) => sum + (emp.overtime || 0), 0);
                    const filteredIndividual = filteredEmployees.reduce((sum, emp) => sum + (emp.individual || 0), 0);
                    return (filteredSalary + filteredIncentive + filteredOvertime + filteredIndividual).toLocaleString();
                  })()}
                </td>
                <td className="py-1 px-1 text-right tabular-nums font-semibold">
                  {(() => {
                    const filteredEmployees = employees.filter(emp => user?.role !== 'employee' || emp.team === userTeamName);
                    const totalNetSalaryWithAllowancesTax = filteredEmployees.reduce((sum, emp) => sum + calculateNetSalaryWithAllowances(emp), 0);
                    const totalAllowancesTax = filteredEmployees.reduce((sum, emp) => sum + calculateDeductions(emp, true).tax, 0);
                    const displayValue = (
                      <>
                        {totalNetSalaryWithAllowancesTax.toLocaleString()}
                        <span className="text-xs text-red-600 ml-1">({totalAllowancesTax.toLocaleString()})</span>
                      </>
                    );
                    return renderSummaryPayrollDataCell(
                      'netSalaryWithTax',
                      displayValue,
                      'font-semibold'
                    );
                  })()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
